import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useCart } from "../../lib/cart";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";
import {
  getCachedHeaderNavigationTree,
  getCategories,
  getHeaderNavigationTree,
  getHomepageConfig,
} from "../../lib/publicConfig";
import { columnsFor, normaliseNavigation, opensPanel } from "../../lib/navigationTree";
import { buildCategoryTree } from "../../lib/categoryTree";
import { useHoverCapable } from "../../hooks/useHoverCapable";
import { adfAssets } from "../storefront/assets";
import { DELIVERY } from "../../content/policies";
import AnnouncementBar from "./AnnouncementBar";
import DesktopNavigation from "./DesktopNavigation";
import HeaderActions from "./HeaderActions";
import MegaMenuPanel from "./MegaMenuPanel";
import MobileMenu from "./MobileMenu";

/* Hardcoded rather than admin-only so the category menu, OUTLET and Endirim
   survive a failed /navigation call or a fresh database. Quruluş sahibin
   2026-10-05 tapşırığıdır: Geyim · Ayaqqabı · Aksesuarlar ayrıca açılan
   bəndlərdir, Kolleksiyalar menyuda yoxdur. */
const NAV = [
  { to: "/collections/new-arrivals", title: { az: "Yeni gələnlər", en: "New arrivals" } },
  { to: "/shop", title: { az: "Geyim", en: "Clothing" } },
  { to: "/categories/ayaqqabi", title: { az: "Ayaqqabı", en: "Shoes" } },
  { to: "/categories/aksesuar", title: { az: "Aksesuarlar", en: "Accessories" } },
  { to: "/outlet", title: { az: "Outlet", en: "Outlet" }, accent: "outlet" },
  { to: "/endirim", title: { az: "Endirim", en: "Sale" }, accent: "sale" },
  { to: "/gallery", title: { az: "Qalereya", en: "Gallery" } },
];

const JOURNAL_NAV_ITEM = { key: "journal", to: "/journal", title: { az: "Jurnal", en: "Journal" }, columns: [] };

const REQUIRED_NAV = [
  { key: "categories", to: "/shop", title: { az: "Geyim", en: "Clothing" }, columns: [], first: true },
  { key: "outlet", to: "/outlet", title: { az: "Outlet", en: "Outlet" }, columns: [], accent: "outlet" },
  { key: "sale", to: "/endirim", title: { az: "Endirim", en: "Sale" }, columns: [], accent: "sale" },
];

const MEGA_PANEL_ID = "adf-mega-panel";
/* Hover intent. Slow enough that a cursor crossing the row on its way somewhere
   else does not flash every panel it passes; the close side is the longer of
   the two so a diagonal path from the label down into the panel survives the
   moment it is over neither. */
const OPEN_DELAY = 150;
const CLOSE_DELAY = 220;

/* Only used when admin has authored no menu: the category shelf, OUTLET and
   Endirim are load-bearing, so an empty table must not remove them. */
function withRequiredLinks(items) {
  const rows = [...(items || [])];
  const has = (link) => rows.some((item) => item.to === link);
  REQUIRED_NAV.forEach((entry) => {
    if (has(entry.to)) return;
    if (entry.first) rows.unshift(entry); else rows.push(entry);
  });
  if (!rows.some((item) => ["/journal", "/blog"].includes(item.to))) rows.push(JOURNAL_NAV_ITEM);
  return rows;
}

export default function Header({ quiet = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { itemCount } = useCart() || { itemCount: 0 };
  const { user } = useAuth() || { user: null };
  const { locale, setLocale, t } = useI18n();
  const hoverCapable = useHoverCapable();

  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaKey, setMegaKey] = useState(null);
  const [condensed, setCondensed] = useState(false);
  // On mobile the bar hides as you scroll down and returns the moment you
  // scroll up, so a small screen keeps its height for the product, not chrome.
  const [hiddenBar, setHiddenBar] = useState(false);
  const [categoryTree, setCategoryTree] = useState([]);
  const [navigation, setNavigation] = useState(() => {
    /* The last menu this browser saw, painted synchronously — otherwise a
       reload shows the hardcoded safety net for a beat and then visibly
       swaps to the real one. */
    const cached = normaliseNavigation(getCachedHeaderNavigationTree());
    return cached.length ? cached : withRequiredLinks(NAV);
  });
  const [announcement, setAnnouncement] = useState(DELIVERY.ribbon);

  const headerRef = useRef(null);
  const barRef = useRef(null);
  const panelRef = useRef(null);
  const searchInput = useRef(null);
  const closeTimer = useRef(null);
  const openTimer = useRef(null);
  const triggers = useRef({});
  const megaKeyRef = useRef(null);
  useEffect(() => { megaKeyRef.current = megaKey; }, [megaKey]);

  const registerTrigger = useCallback((key, node) => { triggers.current[key] = node; }, []);

  const clearTimers = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openTimer.current) clearTimeout(openTimer.current);
    closeTimer.current = null;
    openTimer.current = null;
  }, []);

  const closeMega = useCallback(() => { clearTimers(); setMegaKey(null); }, [clearTimers]);

  /* Hover intent. Without the delays a cursor crossing the row on its way
     somewhere else flickers every panel it passes, and a diagonal path from the
     trigger down into the panel briefly leaves both and snaps it shut. */
  const openMega = useCallback((key, { focusPanel = false } = {}) => {
    clearTimers();
    /* Moving from one open menu to the next is not a new intent — the panel is
       already committed, so it swaps contents immediately. Paying the delay
       again reads as the menu closing and reopening. */
    const immediate = focusPanel || megaKeyRef.current !== null;
    openTimer.current = setTimeout(() => {
      setMegaKey(key);
      if (focusPanel) {
        /* The panel has to be un-hidden before anything inside it can take
           focus, so this waits a beat. A frame is the natural beat, but
           requestAnimationFrame does not fire in a background tab — the
           timeout makes the keyboard path deterministic either way. */
        const focusFirstLink = () => panelRef.current?.querySelector("a")?.focus();
        requestAnimationFrame(focusFirstLink);
        setTimeout(focusFirstLink, 50);
      }
    }, immediate ? 0 : OPEN_DELAY);
  }, [clearTimers]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(() => setMegaKey(null), CLOSE_DELAY);
  }, [clearTimers]);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  const toggleMega = useCallback((key) => {
    clearTimers();
    setMegaKey((current) => (current === key ? null : key));
  }, [clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  /* Anything that sticks below this header needs its height, and the height is
     not a constant. --adf-header-bar-h is the row alone: the mobile sheet is a
     child of this header, so sizing it against the header's own measured height
     would feed back into that measurement. */
  useEffect(() => {
    const node = headerRef.current;
    if (!node) return undefined;
    const publish = () => {
      const root = document.documentElement;
      root.style.setProperty("--adf-header-h", `${Math.round(node.getBoundingClientRect().height)}px`);
      const bar = barRef.current;
      if (bar) {
        const height = bar.getBoundingClientRect().bottom - node.getBoundingClientRect().top;
        root.style.setProperty("--adf-header-bar-h", `${Math.round(height)}px`);
      }
    };
    publish();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", publish);
      return () => window.removeEventListener("resize", publish);
    }
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    if (barRef.current) observer.observe(barRef.current);
    return () => observer.disconnect();
  }, []);

  /* The service notice is read once; past the first screenful the row tightens
     and keeps only what a shopper still needs. */
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setCondensed(y > 80);
      // Direction: hide when scrolling down past the bar, reveal on scroll up.
      if (y > lastY && y > 140) setHiddenBar(true);
      else if (y < lastY - 4) setHiddenBar(false);
      lastY = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // An open menu, search or mega panel always wants the bar present.
  useEffect(() => {
    if (mobileOpen || searchOpen || megaKey) setHiddenBar(false);
  }, [mobileOpen, searchOpen, megaKey]);

  useEffect(() => { if (searchOpen) searchInput.current?.focus(); }, [searchOpen]);

  useEffect(() => {
    const requestSearch = () => { setMobileOpen(false); setSearchOpen(true); };
    window.addEventListener("atelier:open-search", requestSearch);
    return () => window.removeEventListener("atelier:open-search", requestSearch);
  }, []);

  useEffect(() => {
    if (location.pathname === "/shop" && new URLSearchParams(location.search).get("search") === "1") {
      setMobileOpen(false);
      setSearchOpen(true);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    closeMega();
    setMobileOpen(false);
  }, [location.pathname, location.search, closeMega]);

  /* Two stacked panels under one header is not a state anyone wants. */
  useEffect(() => { if (searchOpen) closeMega(); }, [searchOpen, closeMega]);

  useEffect(() => {
    if (!megaKey) return undefined;
    const onPointerDown = (event) => {
      if (!headerRef.current?.contains(event.target)) closeMega();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [megaKey, closeMega]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([
      getHeaderNavigationTree().then((data) => ({ data })),
      getHomepageConfig().then((data) => ({ data })),
      getCategories().then((data) => ({ data })),
    ]).then(([navigationResult, homepageResult, categoriesResult]) => {
      if (!active) return;
      const rows = navigationResult.status === "fulfilled" ? (navigationResult.value.data || []) : [];
      const configured = normaliseNavigation(rows);
      /* Admin owns the menu when it has authored one. The hardcoded set is a
         safety net for a fresh database, not a floor to add on top of an
         editor's deliberate choices. */
      setNavigation(configured.length ? configured : withRequiredLinks(NAV));
      setCategoryTree(
        categoriesResult.status === "fulfilled" ? buildCategoryTree(categoriesResult.value.data) : [],
      );
      const text = homepageResult.status === "fulfilled"
        ? homepageResult.value.data?.announcement_bar_text
        : null;
      if (text) setAnnouncement(text);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  /* Which entries open the panel: whatever admin gave columns, plus the
     catalogue-derived departments behind /shop and a department link. */
  const hasPanel = useCallback(
    (item) => opensPanel(item, categoryTree),
    [categoryTree],
  );

  const openItem = useMemo(
    () => navigation.find((row) => (row.key || row.to) === megaKey) || null,
    [navigation, megaKey],
  );
  const openColumns = useMemo(
    () => (openItem ? columnsFor(openItem, categoryTree) : []),
    [openItem, categoryTree],
  );

  /* Which entry the visitor is currently inside. NavLink only knows its own
     address, so on /categories/ust-geyim — a garment type two levels down — no
     entry in the bar was marked at all and the row gave no sense of place.
     An entry counts as current when the page is its own address or any address
     in its menu. */
  const activeKey = useMemo(() => {
    const path = location.pathname;
    const owns = (item) => {
      if (item.to && item.to !== "/" && (path === item.to || path.startsWith(`${item.to}/`))) return true;
      return columnsFor(item, categoryTree).some((column) => (
        (column.to && column.to === path)
        || (column.links || []).some((child) => child.to === path)
      ));
    };
    const hit = navigation.find(owns);
    return hit ? (hit.key || hit.to) : null;
  }, [navigation, categoryTree, location.pathname]);

  const submitSearch = (event) => {
    event.preventDefault();
    const term = query.trim();
    if (!term) return;
    setSearchOpen(false);
    setMobileOpen(false);
    navigate(`/shop?q=${encodeURIComponent(term)}`);
  };

  /* The reference floats the header over the hero photograph and only gives it
     a ground once the page moves. It applies on the homepage alone, because it
     is the only page that opens with a full-bleed image for white type to sit
     on — anywhere else transparent chrome would be unreadable. Any open panel
     (menu, search, mobile drawer) drops it back to solid, since those need a
     ground of their own. */
  const isHome = location.pathname === "/";
  const overlay = isHome && !condensed && !megaKey && !mobileOpen && !searchOpen;

  return (
    <header
      ref={headerRef}
      data-testid="site-header"
      data-condensed={condensed ? "true" : "false"}
      data-hidden={hiddenBar ? "true" : "false"}
      data-overlay={overlay ? "true" : "false"}
      className={`adf-header z-40 ${isHome
        /* Ana səhifədə header HƏMİŞƏ fixed qalır — yalnız rəngi dəyişir.
           Əvvəl y>80-də fixed→sticky çevrilirdi: sticky axına daxil olub
           bütün səhifəni bir anda öz hündürlüyü qədər itələyirdi — scroll
           zamanı hero-nun "atlanması" bundan idi. */
        ? `fixed top-0 left-0 right-0 ${overlay
            ? "bg-transparent text-white border-b border-white/20"
            : "bg-white text-charcoal border-b border-charcoal/10"}`
        : "sticky top-0 bg-white text-charcoal border-b border-charcoal/10"}`}
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        if (megaKey) {
          const trigger = triggers.current[megaKey];
          closeMega();
          trigger?.focus();
        } else if (mobileOpen) {
          setMobileOpen(false);
        }
      }}
      onBlur={(event) => {
        if (megaKey && !event.currentTarget.contains(event.relatedTarget)) closeMega();
      }}
    >
      <a href="#main" className="adf-skip-link">{t("a11y.skip", "Əsas məzmuna keç")}</a>

      {/* Zolaq unmount olmur — hamar yığılır. Qəfil çıxıb-girməsi sticky
          səhifələrdə məzmunu 28px atladırdı. */}
      <div
        className="adf-announce-collapse"
        data-collapsed={condensed ? "true" : "false"}
        aria-hidden={condensed ? "true" : undefined}
      >
        <div className="adf-announce-collapse__inner">
          <AnnouncementBar text={announcement} />
        </div>
      </div>

      <div ref={barRef} className="adf-container adf-header-bar">
        <Link to="/" data-testid="brand-mark" className="adf-header-brand">
          <img src={adfAssets.logoMono} alt="" className="h-8 w-auto" />
          <img src={adfAssets.logoWord} alt="Atelier Di Falco" className="h-6 w-auto max-w-[150px] lg:h-7 lg:max-w-[168px]" />
        </Link>

        <DesktopNavigation
          items={navigation}
          megaKey={megaKey}
          panelId={MEGA_PANEL_ID}
          quiet={quiet}
          hasPanel={hasPanel}
          activeKey={activeKey}
          hoverCapable={hoverCapable}
          onOpen={openMega}
          onScheduleClose={scheduleClose}
          onCancelClose={cancelClose}
          onToggle={toggleMega}
          registerTrigger={registerTrigger}
        />

        <HeaderActions
          searchOpen={searchOpen}
          onToggleSearch={() => setSearchOpen((open) => !open)}
          locale={locale}
          onToggleLocale={() => setLocale(locale === "az" ? "en" : "az")}
          user={user}
          itemCount={itemCount}
          mobileOpen={mobileOpen}
          onToggleMobile={() => { setMobileOpen((open) => !open); closeMega(); }}
        />
      </div>

      <div onMouseEnter={hoverCapable ? cancelClose : undefined} onMouseLeave={hoverCapable ? scheduleClose : undefined}>
        <MegaMenuPanel
          id={MEGA_PANEL_ID}
          panelRef={panelRef}
          open={Boolean(megaKey)}
          columns={openColumns}
          feature={openItem?.feature || null}
          viewAll={openItem?.to || "/shop"}
          onNavigate={closeMega}
        />
      </div>

      {megaKey && <div className="adf-mega-backdrop" aria-hidden="true" onClick={closeMega} />}

      {searchOpen && (
        <div className="border-t border-[var(--adf-gray-5)] bg-[var(--adf-cream)]" data-testid="search-panel">
          <form onSubmit={submitSearch} className="adf-container flex items-center gap-3 py-4" role="search">
            <Search size={18} strokeWidth={1.4} className="shrink-0 text-[var(--adf-gray-3)]" aria-hidden="true" />
            <input
              ref={searchInput}
              type="search"
              name="q"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("search.placeholder", "Parça, material və ya kolleksiya axtarın")}
              aria-label={t("search.aria", "Məhsul axtar")}
              data-testid="search-input"
              className="min-w-0 flex-1 bg-transparent py-2 text-[15px] outline-none placeholder:text-[var(--adf-gray-3)]"
            />
            <button type="submit" className="adf-cta adf-cta-dark shrink-0 px-5 py-2" data-testid="search-submit">
              {t("search.submit", "Axtar")}
            </button>
          </form>
        </div>
      )}

      <MobileMenu
        open={mobileOpen}
        items={navigation}
        tree={categoryTree}
        categoryTree={categoryTree}
        user={user}
        locale={locale}
        onToggleLocale={() => setLocale(locale === "az" ? "en" : "az")}
        onClose={() => setMobileOpen(false)}
      />
    </header>
  );
}
